import { Routes, Route, Navigate } from 'react-router-dom';
import { LandingPage } from './components/landing/LandingPage';
import { LoginPage } from './components/auth/LoginPage';
import { SignUpPage } from './components/auth/SignUpPage';
import { AuthCallback } from './components/auth/AuthCallback';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { OnboardingPage } from './components/onboarding/OnboardingPage';
import { PricingPage } from './components/pricing/PricingPage';
import { useAuth } from './context/AuthContext';
import { useOrganization } from './hooks/useOrganization';
import { CheckoutSuccess } from './components/checkout/CheckoutSuccess';
import { ROUTES } from './config/constants';
import { PrivacyPolicy } from './pages/legal/PrivacyPolicy';
import { DataSubjectRights } from './components/gdpr/DataSubjectRights';
import { ConsentManagement } from './components/gdpr/ConsentManagement';
import { CookiePolicy } from './pages/legal/CookiePolicy';

export default function App() {
  const { session, loading: authLoading } = useAuth();
  const { hasOrganization, loading: orgLoading } = useOrganization();

  // Show loading state while checking auth and organization status
  if (authLoading || (session && orgLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={
          !session ? (
            <LandingPage />
          ) : hasOrganization ? (
            <Navigate to={ROUTES.DASHBOARD} />
          ) : (
            <Navigate to={ROUTES.ONBOARDING} />
          )
        } 
      />
      <Route 
        path="/login" 
        element={
          !session ? (
            <LoginPage />
          ) : hasOrganization ? (
            <Navigate to={ROUTES.DASHBOARD} />
          ) : (
            <Navigate to={ROUTES.ONBOARDING} />
          )
        } 
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      
      {/* Auth callback route - accessible regardless of session state */}
      <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Legal and GDPR routes - publicly accessible */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/data-rights" element={<DataSubjectRights />} />
        <Route path="/consent-management" element={<ConsentManagement />} />

      {/* Onboarding route - for authenticated users without organizations */}
      <Route
        path="/onboarding"
        element={
          session ? (
            hasOrganization ? (
              <Navigate to={ROUTES.DASHBOARD} />
            ) : (
              <OnboardingPage />
            )
          ) : (
            <Navigate to={ROUTES.LOGIN} />
          )
        }
      />

      {/* Protected routes - for authenticated users with organizations with organization */}
      <Route
        path="/dashboard/*"
        element={
          session ? (
            hasOrganization ? (
              <DashboardLayout />
            ) : (
              <Navigate to={ROUTES.ONBOARDING} />
            )
          ) : (
            <Navigate to={ROUTES.LOGIN} />
          )
        }
      />
      
      <Route
        path="/checkout/success"
        element={<CheckoutSuccess />}
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}